import React, { useCallback, useEffect, useState } from 'react';
import './ServiceOutagePanel.css';

const API = '';

function portKey(board, port) {
  return `${board}:${port}`;
}

function buildEmptySelection(olts) {
  const sel = {};
  (olts || []).forEach((olt) => {
    sel[olt.oltId] = { wholeOlt: false, ports: {} };
    (olt.ports || []).forEach((p) => {
      sel[olt.oltId].ports[portKey(p.board, p.port)] = false;
    });
  });
  return sel;
}

function applyOutageToSelection(outage, olts) {
  const sel = buildEmptySelection(olts);
  (outage?.areas || []).forEach((area) => {
    if (!sel[area.oltId]) {
      sel[area.oltId] = { wholeOlt: false, ports: {} };
    }
    sel[area.oltId].wholeOlt = !!area.wholeOlt;
    (area.ports || []).forEach((p) => {
      sel[area.oltId].ports[portKey(p.board, p.port)] = true;
    });
  });
  return sel;
}

function selectionToAreas(olts, selection) {
  return (olts || []).map((olt) => {
    const state = selection[olt.oltId] || { wholeOlt: false, ports: {} };
    const ports = (olt.ports || [])
      .filter((p) => state.ports[portKey(p.board, p.port)])
      .map((p) => ({ board: p.board, port: p.port }));
    return {
      oltId: olt.oltId,
      oltName: olt.oltName,
      wholeOlt: !!state.wholeOlt,
      ports
    };
  }).filter((a) => a.wholeOlt || a.ports.length);
}

export default function ServiceOutagePanel({ onClose }) {
  const [olts, setOlts] = useState([]);
  const [outage, setOutage] = useState(null);
  const [message, setMessage] = useState('');
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    setLoading(true);
    setFeedback('');
    try {
      const [optRes, outRes] = await Promise.all([
        fetch(`${API}/api/service-outage/options`, { headers }),
        fetch(`${API}/api/service-outage`, { headers })
      ]);
      const optData = await optRes.json();
      const outData = await outRes.json();
      const oltList = optData.olts || [];
      const current = outData.outage || {};
      setOlts(oltList);
      setOutage(current);
      setMessage(current.message || '');
      setSelection(applyOutageToSelection(current, oltList));
    } catch (e) {
      setFeedback('Error cargando datos del panel');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleWholeOlt = (oltId) => {
    setSelection((prev) => ({
      ...prev,
      [oltId]: {
        ...(prev[oltId] || { ports: {} }),
        wholeOlt: !prev[oltId]?.wholeOlt
      }
    }));
  };

  const togglePort = (oltId, board, port) => {
    const key = portKey(board, port);
    setSelection((prev) => ({
      ...prev,
      [oltId]: {
        wholeOlt: false,
        ports: {
          ...(prev[oltId]?.ports || {}),
          [key]: !prev[oltId]?.ports?.[key]
        }
      }
    }));
  };

  const handleActivate = async () => {
    const areas = selectionToAreas(olts, selection);
    if (!message.trim()) {
      setFeedback('Escribe el mensaje que Diana enviará a los clientes afectados');
      return;
    }
    if (!areas.length) {
      setFeedback('Selecciona la OLT completa o al menos un puerto');
      return;
    }
    setSaving(true);
    setFeedback('Activando fallo de servicio...');
    try {
      const r = await fetch(`${API}/api/service-outage/activate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: message.trim(), areas })
      });
      const data = await r.json();
      if (data.success) {
        setOutage(data.outage);
        setFeedback('Fallo activado. Diana enviará tu mensaje a clientes de esas zonas.');
      } else {
        setFeedback(data.error || 'No se pudo activar');
      }
    } catch (e) {
      setFeedback('Error de conexión');
    }
    setSaving(false);
  };

  const handleDeactivate = async () => {
    if (!window.confirm('¿Desactivar el fallo de servicio? Diana volverá al diagnóstico normal.')) return;
    setSaving(true);
    setFeedback('Desactivando...');
    try {
      const r = await fetch(`${API}/api/service-outage/deactivate`, { method: 'POST', headers });
      const data = await r.json();
      if (data.success) {
        setOutage(data.outage);
        setFeedback('Fallo desactivado. Diana volverá al flujo normal.');
      } else {
        setFeedback(data.error || 'No se pudo desactivar');
      }
    } catch (e) {
      setFeedback('Error de conexión');
    }
    setSaving(false);
  };

  return (
    <div className="service-outage-overlay" onClick={(e) => { if (e.target.classList.contains('service-outage-overlay')) onClose(); }}>
      <div className="service-outage-panel">
        <div className="service-outage-header">
          <div>
            <h2>⚠️ Fallo de Internet por Zona</h2>
            <p>Selecciona OLT/puertos afectados y el mensaje que Diana enviará</p>
          </div>
          <button type="button" className="service-outage-close" onClick={onClose}>✕</button>
        </div>

        <div className="service-outage-body">
          {loading ? (
            <div className="service-outage-loading">Cargando OLTs y puertos...</div>
          ) : (
            <>
              <div className={`service-outage-status ${outage?.active ? 'active' : 'inactive'}`}>
                {outage?.active
                  ? `🔴 ACTIVO — Diana envía tu mensaje a clientes sin internet en las zonas seleccionadas`
                  : '🟢 Inactivo — Diana hace el diagnóstico normal'}
              </div>

              <label className="service-outage-label" htmlFor="outage-message">
                Mensaje para clientes afectados
              </label>
              <textarea
                id="outage-message"
                className="service-outage-message"
                placeholder="Ej: Estamos trabajando en una avería en tu zona. El servicio se restablecerá en las próximas horas. Disculpa las molestias."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="service-outage-hint">
                Cuando un cliente diga que no tiene internet, Diana primero responderá:
                {' '}
                <em>Voy a verificar su conexión, un momento por favor...</em>
                {' '}
                Luego, si pertenece a una zona seleccionada, enviará este mensaje.
              </div>

              {olts.map((olt) => {
                const state = selection[olt.oltId] || { wholeOlt: false, ports: {} };
                return (
                  <div key={olt.oltId} className="service-outage-olt-block">
                    <div className="service-outage-olt-title">
                      <h3>{olt.oltName}</h3>
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>ID {olt.oltId}</span>
                    </div>

                    <label className="service-outage-whole">
                      <input
                        type="checkbox"
                        checked={!!state.wholeOlt}
                        onChange={() => toggleWholeOlt(olt.oltId)}
                      />
                      OLT completa (todos los puertos)
                    </label>

                    <div className="service-outage-label">Board / Port</div>
                    <div className="service-outage-ports">
                      {(olt.ports || []).map((p) => {
                        const key = portKey(p.board, p.port);
                        const checked = !!state.ports[key];
                        return (
                          <label
                            key={key}
                            className={`service-outage-port ${state.wholeOlt ? 'disabled' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!!state.wholeOlt}
                              onChange={() => togglePort(olt.oltId, p.board, p.port)}
                            />
                            B{p.board} / P{p.port}
                            <span style={{ color: '#64748b', marginLeft: 'auto' }}>({p.onuCount})</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="service-outage-actions">
                <button
                  type="button"
                  className="service-outage-btn activate"
                  onClick={handleActivate}
                  disabled={saving}
                >
                  {saving ? 'Guardando...' : 'Activar fallo de servicio'}
                </button>
                {outage?.active && (
                  <button
                    type="button"
                    className="service-outage-btn deactivate"
                    onClick={handleDeactivate}
                    disabled={saving}
                  >
                    Desactivar fallo
                  </button>
                )}
              </div>

              {feedback && <div className="service-outage-feedback">{feedback}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
